<template>
    <div class="solve-mfa">
        <h2>Please enter your authentication code</h2>

        <p v-if="!isLoading">
            <span v-if="mfaType === 'app'">
                In order to log into this account,
                you will need to provide your Multi-Factor Authentication (MFA) code from the Microsoft Authenticator app.
            </span>

            <span v-if="mfaType === 'sms'">
                In order to log into this account,
                you will need to provide your Multi-Factor Authentication (MFA) code we sent via SMS to the phone number provided.
            </span>

            <span v-if="mfaType === 'voice'">
                In order to log into this account,
                you will need to provide your Multi-Factor Authentication (MFA) code we sent via phone call to the phone number provided.
            </span>
        </p>

        <div v-if="!isLoading">
            <div class="row">
                <div class="col-md-6">
                    <div class="form-group">
                        <input type="text"
                               v-model="form.authenticator_code"
                               placeholder="Code"
                               class="form-control"
                               @keydown="handleConfirmViaEnter"
                        >
                        <div class="error-help" v-if="errorMessage.length > 0">{{ errorMessage }}</div>
                    </div>
                </div>
            </div>
            <button class="btn btn-primary" :disabled="v$.$invalid" @click="handleConfirm">
                Verify code
            </button>
            <p class="resend-text" v-if="mfaType !== 'app'">
                If you did not receive the code, <a href="javascript:void(0)" @click="handleRequestMfaCode">Resend it.</a>
            </p>
        </div>
        <div v-else>
            <Loader wait-text="Verifying..."/>
        </div>
    </div>
</template>

<script>
import required from "vuelidate/lib/validators/required";
import { useForm } from "@/hooks/use-form";
import { useLoading } from "@/hooks/use-loading";
import Loader from "@/components/Loader";
import { onMounted, ref } from "vue";
import { requestMfaCode, resolveLoginAction } from "@/api/login";
import { minLength } from "@vuelidate/validators";
import { userState } from "@/stores/user";
import { TYPE_MFA_APP } from "@/pages/pending-actions/mfa-types";

export default {
    name: "SolveMfa",
    components: { Loader },
    setup(props, { emit }) {
        const { isLoading, startLoading, stopLoading } = useLoading(false);

        const errorMessage = ref('');
        const mfaType = ref('');

        const { form, v$ } = useForm({
            props: {
                authenticator_code: ''
            },
            rules: {
                authenticator_code: {
                    required,
                    minLength: minLength(6),
                }
            }
        });

        const handleConfirm = async () => {
            if (v$.value.$invalid) {
                return;
            }

            errorMessage.value = '';

            startLoading();
            try {
                await resolveLoginAction({
                    action: 'mfa_code_required',
                    data: {
                        code: form.authenticator_code,
                    }
                });

                emit('action-handled');
            } catch (e) {
                errorMessage.value = e?.response?.data?.message ?? 'Unknown error';
                stopLoading();
            }
        };

        const handleConfirmViaEnter = (e) => {
            if (e.key === "Enter" || e.key === "Return") {
                handleConfirm();
            }
        };

        const handleRequestMfaCode = async () => {
            errorMessage.value = '';

            try {
                await requestMfaCode({ type: mfaType.value });
            } catch (e) {
                console.error(e);
            }
        }

        onMounted(() => {
            const { data } = userState.pending_actions.find((item) => item.type === 'mfa_code_required');

            mfaType.value = data.type;

            if (mfaType.value !== TYPE_MFA_APP) {
                handleRequestMfaCode();
            }
        });

        return {
            isLoading,
            errorMessage,
            mfaType,
            handleConfirmViaEnter,
            handleConfirm,
            handleRequestMfaCode,
            form,
            v$,
        }
    }
}
</script>

<style scoped lang="scss">
.solve-mfa {
    h2 {
        font-size: 16px;
        font-weight: bold;
    }

    .app-buttons {
        text-align: center;
        padding-top: 15px;
        padding-bottom: 15px;

        a {
            display: inline-block;
            margin-right: 5px;
        }
    }

    p {
        font-size: 12px;
    }

    .error-help {
        color: red;
    }

    .resend-text {
        margin-top: 10px;

        a {
            font-style: italic;
        }
    }
}
</style>